/**
 * Web Push client — subscription lifecycle + permission helpers.
 *
 *   await enablePush();     // request permission + register + upsert on server
 *   await disablePush();    // unregister from browser + clear server record
 *   pushState();            // synchronous snapshot: {supported, permission, subscribed}
 *
 * The flow:
 *   1. Ensure Notification.permission === "granted" (triggers the browser
 *      permission prompt only if it's still "default" — repeatedly asking
 *      a "denied" user is both futile and a dark pattern).
 *   2. Get the active service worker registration.
 *   3. Ask PushManager for an existing subscription OR subscribe a new
 *      one using our VAPID public key.
 *   4. POST the subscription (endpoint + p256dh + auth) to PB.
 *   5. Fire a status beacon so the admin view reflects the change.
 */

import { pbFor } from "./api/router";
import { getDeviceId, reportPwaStatus } from "./pwa";

/**
 * VAPID public key is fetched at runtime from `/api/cl/push/config` the
 * first time we need it. Keeping it out of the frontend build means:
 *   - key rotation doesn't require a rebuild / redeploy
 *   - the Vite image doesn't need a GH secret wired through
 * The public key is literally designed to be public, so serving it from
 * a PB route has the same trust properties as baking it in at build.
 */
let cachedVapidKey: string | null = null;
let vapidFetchInFlight: Promise<string> | null = null;

async function fetchVapidKey(): Promise<string> {
  if (cachedVapidKey !== null) return cachedVapidKey;
  if (!vapidFetchInFlight) {
    vapidFetchInFlight = pbFor("notifications")
      .send<{ vapidPublicKey: string; enabled: boolean }>("/api/cl/push/config", { method: "GET" })
      .then((res) => { cachedVapidKey = res?.vapidPublicKey || ""; return cachedVapidKey; })
      .catch(() => { cachedVapidKey = ""; return ""; })
      .finally(() => { vapidFetchInFlight = null; });
  }
  return vapidFetchInFlight;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  // Web Push expects a padded base64url key; the Vite env holds it as
  // a single string we pass straight through.
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function toB64(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export interface PushState {
  /** Browser supports Web Push + Notification + service workers. */
  supported: boolean;
  /** Current Notification permission state. */
  permission: NotificationPermission;
  /** True when a PushSubscription exists locally. */
  subscribed: boolean;
  /** True when VAPID key hasn't been compiled in. */
  missingKey: boolean;
}

export function pushState(): PushState {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
  return {
    supported,
    permission:
      typeof Notification !== "undefined" ? Notification.permission : "denied",
    subscribed: false, // filled async via getSubscription()
    // We optimistically assume a key is available; enablePush() is the
    // authoritative check (it fetches the key from the server).
    missingKey: false,
  };
}

/**
 * iOS-specific gate. Apple requires the PWA to be installed to the
 * home screen AND opened in standalone mode before Web Push can be
 * enabled — taps on a Notification.requestPermission() call from a
 * regular Safari tab silently fail. This helper lets the UI show
 * install instructions instead of a dead button.
 */
export function iosPushStatus(): "ok" | "install-required" | "unsupported" | "not-ios" {
  if (typeof window === "undefined") return "not-ios";
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ identifies as desktop Safari; detect via touch + mac UA.
    (ua.includes("Macintosh") && "ontouchend" in document);
  if (!isIOS) return "not-ios";

  // iOS 16.4+ is the minimum for Web Push. Earlier versions: no.
  const m = ua.match(/OS (\d+)_(\d+)/);
  const major = m ? parseInt(m[1], 10) : 0;
  const minor = m ? parseInt(m[2], 10) : 0;
  if (major < 16 || (major === 16 && minor < 4)) return "unsupported";

  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return standalone ? "ok" : "install-required";
}

export async function getActiveSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

export async function enablePush(): Promise<{
  ok: boolean;
  reason?: "unsupported" | "denied" | "no-key" | "error";
  message?: string;
}> {
  const s = pushState();
  if (!s.supported) return { ok: false, reason: "unsupported" };

  const vapidKey = await fetchVapidKey();
  if (!vapidKey) return { ok: false, reason: "no-key", message: "Push is not enabled on the server yet." };

  // Ask for permission only if still undecided.
  if (s.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return { ok: false, reason: "denied" };
  } else if (s.permission !== "granted") {
    return { ok: false, reason: "denied" };
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    const json = sub.toJSON() as { endpoint: string; keys: { p256dh?: string; auth?: string } };
    const p256dh = json.keys?.p256dh || toB64(sub.getKey("p256dh"));
    const auth   = json.keys?.auth   || toB64(sub.getKey("auth"));

    await pbFor("notifications").send("/api/cl/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: { p256dh, auth },
        deviceId: getDeviceId(),
        ua: navigator.userAgent.slice(0, 180),
        platform: detectPlatform(),
      }),
    });

    await reportPwaStatus({ pushPermission: "granted" });
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: "error", message: err instanceof Error ? err.message : String(err) };
  }
}

export async function disablePush(): Promise<void> {
  try {
    const sub = await getActiveSubscription();
    if (sub) await sub.unsubscribe();
  } catch { /* ignore */ }
  try {
    await pbFor("notifications").send("/api/cl/push/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: getDeviceId() }),
    });
  } catch { /* ignore */ }
  await reportPwaStatus();
}

function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Macintosh/.test(ua)) return "mac";
  if (/Windows/.test(ua)) return "windows";
  if (/Linux/.test(ua)) return "linux";
  return "web";
}
